import React from 'react';
import Iconify from 'src/components/Iconify';
import { ITEM_VAZIO } from '../propostaRapida';
import { calcularItem, calcularTotais } from './orcamentoModel';
import { ORCAMENTO_DESIGN as D } from './Orcamento.design';

export function formatCurrency(value) {
  if (value === undefined || value === null || Number.isNaN(value)) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

// Moeda no padrão brasileiro: os dígitos entram pela direita ("125000" vira
// "R$ 1.250,00"). Guarda o número; mostra sempre formatado.
function CurrencyInput({ value, onChange, className, disabled, ariaLabel }) {
  const handleChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '');
    onChange(digits ? parseInt(digits, 10) / 100 : 0);
  };
  return (
    <input
      type="text"
      inputMode="numeric"
      value={formatCurrency(value)}
      onChange={handleChange}
      className={className}
      disabled={disabled}
      aria-label={ariaLabel}
    />
  );
}

const somenteInteiro = (texto) => parseInt(String(texto).replace(/\D/g, ''), 10) || 0;

// Tabela de itens do orçamento. Campos "silenciosos" (borda só no foco) para a
// linha ler como tabela. O desconto por item não aparece aqui — orçamento usa
// só o desconto geral — mas um valor já gravado é preservado e entra no total.
export default function OrcamentoItens({ itens, descontoGlobalPercent, onChangeItens, onChangeDesconto, readOnly }) {
  const lista = itens || [];

  const atualizar = (index, patch) => onChangeItens(lista.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  const adicionar = () => onChangeItens([...lista, { ...ITEM_VAZIO }]);
  // A última linha não some: vira uma linha vazia, para a tabela nunca ficar sem
  // lugar para digitar.
  const remover = (index) => onChangeItens(lista.length === 1 ? [{ ...ITEM_VAZIO }] : lista.filter((_, i) => i !== index));

  const { subtotal, total } = calcularTotais(lista, descontoGlobalPercent);

  return (
    <>
      <div className={D.items}>
        <div className={`${D.itemsRow} ${D.itemsHead}`} aria-hidden="true">
          <span>Descrição</span>
          <span className="text-center">Qtd.</span>
          <span className="text-right pr-2.5">Unitário</span>
          <span className="text-right pr-2.5">Total</span>
          <span />
        </div>

        {lista.map((item, index) => (
          <div key={index} className={`${D.itemsRow} ${D.itemsRowLine}`}>
            <input
              className={D.quiet}
              placeholder="Produto ou serviço"
              value={item.descricao || ''}
              onChange={e => atualizar(index, { descricao: e.target.value })}
              disabled={readOnly}
              aria-label={`Descrição do item ${index + 1}`}
            />
            <input
              className={`${D.quiet} ${D.quietQty}`}
              inputMode="numeric"
              value={item.quantidade}
              onChange={e => atualizar(index, { quantidade: somenteInteiro(e.target.value) })}
              disabled={readOnly}
              aria-label={`Quantidade do item ${index + 1}`}
            />
            <CurrencyInput
              className={`${D.quiet} ${D.quietNum}`}
              value={item.preco_unitario}
              onChange={v => atualizar(index, { preco_unitario: v })}
              disabled={readOnly}
              ariaLabel={`Preço unitário do item ${index + 1}`}
            />
            <span className={D.itemTotal}>{formatCurrency(calcularItem(item))}</span>
            {readOnly ? (
              <span />
            ) : (
              <button type="button" className={D.remove} onClick={() => remover(index)} title="Remover item" aria-label={`Remover item ${index + 1}`}>
                <Iconify icon="eva:close-outline" width={15} />
              </button>
            )}
          </div>
        ))}

        {!readOnly && (
          <button type="button" className={D.add} onClick={adicionar}>
            <Iconify icon="eva:plus-fill" width={16} />
            Adicionar item
          </button>
        )}
      </div>

      <div className={D.sum}>
        <span className={D.sumLabel}>Subtotal</span>
        <span className={D.sumValue}>{formatCurrency(subtotal)}</span>

        <span className={D.sumLabel}>Desconto</span>
        <span className={D.discWrap}>
          <input
            className={`${D.input} ${D.discInput}`}
            inputMode="numeric"
            value={descontoGlobalPercent || 0}
            onChange={e => onChangeDesconto(Math.min(100, somenteInteiro(e.target.value)))}
            disabled={readOnly}
            aria-label="Desconto geral em porcentagem"
          />
          <span className={D.sumLabel}>%</span>
        </span>

        <span className={D.sumRule} />

        <span className={D.sumBig}>Total</span>
        <span className={D.sumBigValue}>{formatCurrency(total)}</span>
      </div>
    </>
  );
}
